// Investigation for manual enrolment

import { myAccountClient } from "../../api/myAccountClient";
import {
    createPasskeyCredential,
    isPlatformAuthenticatorAvailable,
    ALREADY_ENROLLED,
    UNSUPPORTED,
    USER_CANCELLED,
    PasskeyCeremonyError
} from "../../api/webauthn";
import {
    PASSKEY_FLOW_RESET,
    PASSKEY_ENROLLMENT_STARTED,
    PASSKEY_ENROLLMENT_SUCCEEDED,
    PASSKEY_ENROLLMENT_FAILED,
    PASSKEY_ENROLLMENT_UNSUPPORTED,
    PasskeyFlowActionType
} from "../../domain/passkeyFlow/index";

export const passkeyEnrollmentStartedAction = (): PasskeyFlowActionType => {
    return { type: PASSKEY_ENROLLMENT_STARTED, payload: null };
};

export const passkeyEnrollmentSucceededAction = (): PasskeyFlowActionType => {
    return { type: PASSKEY_ENROLLMENT_SUCCEEDED, payload: null };
};

export const passkeyEnrollmentFailedAction = (errorCode: string): PasskeyFlowActionType => {
    return { type: PASSKEY_ENROLLMENT_FAILED, payload: errorCode };
};

export const passkeyEnrollmentUnsupportedAction = (): PasskeyFlowActionType => {
    return { type: PASSKEY_ENROLLMENT_UNSUPPORTED, payload: null };
};

/**
 * Returns the slice to `idle`. The store is global, so without this a previous
 * visit's `enrolled`/`unsupported` status would make the screen leave again
 * immediately on its next mount.
 */
export const handlePasskeyFlowReset = (): PasskeyFlowActionType => {
    return { type: PASSKEY_FLOW_RESET, payload: null };
};

/**
 * Checks up-front whether this device can do platform passkeys.
 *
 * Running this before showing the pitch avoids inviting a user to create a
 * passkey on a machine that has no authenticator, which would end in a
 * confusing native error dialog.
 */
export const handleCheckPasskeySupport = () => {
    return function (dispatch: any) {
        return isPlatformAuthenticatorAvailable()
            .then((available) => {
                if (!available) {
                    dispatch(passkeyEnrollmentUnsupportedAction());
                }
                return available;
            })
            .catch(() => {
                dispatch(passkeyEnrollmentUnsupportedAction());
                return false;
            });
    };
};

/**
 * Runs the full enrollment: challenge -> browser ceremony -> verify.
 *
 * @param token An access token for the My Account API audience. Must belong to
 *   the signed-in user; client credentials are rejected by Auth0.
 * @param connection The database connection the passkey should belong to,
 *   forwarded by the Auth0 action as a query parameter.
 */
export const handlePasskeyEnrollment = (token: string, connection: string) => {
    return function (dispatch: any) {
        dispatch(passkeyEnrollmentStartedAction());

        const endpoint = new myAccountClient();

        return endpoint
            .startPasskeyEnrollment(token, connection)
            .then((challenge) => {
                return createPasskeyCredential(challenge.authn_params_public_key).then((authnResponse) =>
                    endpoint.verifyPasskeyEnrollment(token, challenge.auth_session, authnResponse)
                );
            })
            .then(() => {
                dispatch(passkeyEnrollmentSucceededAction());
            })
            .catch((error: any) => {
                // `InvalidStateError` surfaces here as ALREADY_ENROLLED. It means
                // the authenticator refused to create a duplicate because this
                // device ALREADY holds a passkey for the account - which is
                // exactly the outcome we wanted. Treat it as success so the
                // action records the device and stops prompting.
                if (error instanceof PasskeyCeremonyError && error.code === ALREADY_ENROLLED) {
                    dispatch(passkeyEnrollmentSucceededAction());
                    return;
                }

                if (error instanceof PasskeyCeremonyError && error.code === UNSUPPORTED) {
                    dispatch(passkeyEnrollmentUnsupportedAction());
                    return;
                }

                // The user dismissed the native dialog. Not an error worth
                // shouting about - drop back to idle so they can retry or skip.
                if (error instanceof PasskeyCeremonyError && error.code === USER_CANCELLED) {
                    dispatch(passkeyEnrollmentFailedAction("passkey_cancelled"));
                    return;
                }

                console.log(error);
                dispatch(passkeyEnrollmentFailedAction("passkey_enrollment_failed"));
            });
    };
};
